import { useState, useEffect } from 'react';
import type { ThemeServiceError } from '../../services/themeService';

interface ThemeErrorNotificationProps {
  error: ThemeServiceError | null;
  onDismiss?: () => void;
  autoHide?: boolean;
  autoHideDelay?: number;
}

export function ThemeErrorNotification({ 
  error, 
  onDismiss, 
  autoHide = true, 
  autoHideDelay = 5000 
}: ThemeErrorNotificationProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (error) {
      setIsVisible(true);
      
      if (autoHide) {
        const timer = setTimeout(() => {
          setIsVisible(false);
          setTimeout(() => onDismiss?.(), 300); // Wait for fade out animation
        }, autoHideDelay);
        
        return () => clearTimeout(timer);
      }
    } else {
      setIsVisible(false);
    }
  }, [error, autoHide, autoHideDelay, onDismiss]);

  if (!error) return null;

  const getErrorIcon = () => {
    switch (error.type) {
      case 'validation':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      case 'storage':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
          </svg>
        );
      case 'network':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
    }
  };

  const getErrorColor = () => {
    switch (error.type) {
      case 'validation':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'storage':
        return 'bg-orange-50 border-orange-200 text-orange-800';
      case 'network':
        return 'bg-red-50 border-red-200 text-red-800';
      default:
        return 'bg-red-50 border-red-200 text-red-800';
    }
  };

  const getErrorTitle = () => {
    switch (error.type) {
      case 'validation':
        return 'Theme Validation Error';
      case 'storage':
        return 'Storage Error';
      case 'network':
        return 'Network Error';
      default:
        return 'Theme Error';
    }
  };

  return (
    <div
      className={`
        fixed top-4 right-4 max-w-md w-full z-50 transition-all duration-300 transform
        ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
    >
      <div className={`rounded-lg border p-4 shadow-lg ${getErrorColor()}`}>
        <div className="flex items-start">
          <div className="flex-shrink-0">
            {getErrorIcon()}
          </div>
          
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium">
              {getErrorTitle()}
            </h3>
            <p className="mt-1 text-sm opacity-90">
              {error.message}
            </p>
            
            {error.details && Array.isArray(error.details) && error.details.length > 0 && (
              <div className="mt-2">
                <details className="text-xs">
                  <summary className="cursor-pointer hover:underline">
                    View details ({error.details.length} issues)
                  </summary>
                  <ul className="mt-1 space-y-1 list-disc list-inside">
                    {error.details.slice(0, 5).map((detail: any, index: number) => (
                      <li key={index}>
                        {detail.field}: {detail.message}
                      </li>
                    ))}
                    {error.details.length > 5 && (
                      <li>... and {error.details.length - 5} more</li>
                    )}
                  </ul>
                </details>
              </div>
            )}
          </div>
          
          <div className="ml-4 flex-shrink-0">
            <button
              onClick={() => {
                setIsVisible(false);
                setTimeout(() => onDismiss?.(), 300);
              }}
              className="inline-flex rounded-md p-1.5 hover:bg-black hover:bg-opacity-10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-current"
            >
              <span className="sr-only">Dismiss</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}